<script setup lang="ts">

import {Ref, ref, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import ClockIcon from "@/assets/widgetIcons/ClockIcon.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {QInput, QPopupProxy, QSelect} from "quasar";
import {focusAndSelectInput, NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";

let props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
let fieldUtil = new FieldWidgetUtil(props.presenter, props.fieldMetaData);
const emit = defineEmits<NamaNavigateToNextEmit>()

let inputTime = fieldUtil.formattedValue();
const selectedTime: Ref<any | null> = ref(inputTime.value);
const timeProxy = ref<QPopupProxy>();
const allowedValues = ClientCache.getAllowedValuesFor(props.presenter.entityType, props.fieldMetaData.fieldId)
const isRestricted = ClientCache.isFieldValuesRestricted(props.presenter.entityType, props.fieldMetaData.fieldId)
const hasAllowedValues = ObjectChecker.isNotEmptyOrNull(allowedValues)

watch(
  () => inputTime.value,
  (value) => {
    selectedTime.value = value
  }
);

function onTimeSelect(value: any) {
  fieldUtil.onPartialUserInput(value);
  selectedTime.value = value;
}

const input = ref<QInput | QSelect>()

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function onClockIconClick(e) {
  if (isRestricted)
    return;
  if (timeProxy.value)
    timeProxy.value.toggle(e)
}

function onF4KeyClicked(e) {
  if (isRestricted)
    return;
  if (timeProxy.value)
    timeProxy.value.toggle(e)
}

function onSelect(value?: string) {
  fieldUtil.onPartialUserInput(value);
  selectedTime.value = value;
  fieldUtil.onBlur();
}

function onEnterKeyDown(e: any, doNotGoToNextWithEnter?: boolean) {
  if (doNotGoToNextWithEnter)
    return;
  emit('navigateToNext', {keyboardEvent:e});
}

function onDoubleClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  timeProxy.value.show()
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <q-select
        v-if="hasAllowedValues" :options="allowedValues" hide-bottom-space outlined dense options-dense use-input hide-selected fill-input
        :model-value="inputTime" @update:model-value="onSelect"
        @input-value="fieldUtil.onPartialUserInput"
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)" ref="input"
        @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
        :id="fieldId"
        autocomplete="off"
        input-debounce="0"
        :type="showAsPassword ? 'password' : 'text'"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        class="nama-customize-dense-input"
        :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
        input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
        options-selected-class="tw-text-base active-white tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white nama-icon-active"
        popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
        @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
        @keydown.f4.stop="onF4KeyClicked">
        <template v-slot:append>
          <q-avatar square name="event" class="nama-avatar-hover tw-size-7 tw-cursor-pointer">
            <ClockIcon class="tw-size-4 nama-widget-icon" @click.stop="onClockIconClick"
                       :class="isRestricted ? 'tw-opacity-30 tw-cursor-default' : 'nama-widget-icon-hover tw-cursor-pointer'"/>
            <q-popup-proxy ref="timeProxy" cover no-parent-event class="tw-bg-transparent ag-custom-component-popup" transition-show="scale"
                           transition-hide="scale"
                           @hide="fieldUtil.onUserInputFinished"
                           @keydown.f4.stop="onF4KeyClicked">
              <q-time :model-value="selectedTime" @update:model-value="onTimeSelect" format24h now-btn
                      :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                      class="tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-white dark:tw-bg-namaBackground-dim-dark">
                <div class="tw-bg-transparent row items-center justify-end">
                  <q-btn v-close-popup :label="Translator.translateId('close')"
                         class="tw-text-primary-light dark:tw-text-primary-dark tw-bg-transparent" flat/>
                </div>
              </q-time>
            </q-popup-proxy>
          </q-avatar>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-select>

      <q-input
        v-else hide-bottom-space outlined dense :model-value="inputTime" @update:model-value="fieldUtil.onPartialUserInput"
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
        @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
        :id="fieldId" ref="input"
        autocomplete="off"
        @dbclick="onDoubleClick"
        :type="showAsPassword ? 'password' : 'text'"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        class="nama-customize-dense-input nama-focused-input nama-dark-popup"
        :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
        input-class="nama-input-class"
        for="timeFieldWidget"
        @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
        @keydown.f4.stop="onF4KeyClicked">
        <template v-slot:append>
          <q-avatar square name="event" class="nama-avatar-hover tw-size-7 tw-cursor-pointer">
            <ClockIcon class="tw-size-4 nama-widget-icon-hover nama-widget-icon"/>
            <q-popup-proxy ref="timeProxy" cover class="tw-bg-transparent ag-custom-component-popup" transition-show="scale" transition-hide="scale"
                           @hide="fieldUtil.onUserInputFinished"
                           @keydown.f4.stop="onF4KeyClicked">
              <q-time :model-value="selectedTime" @update:model-value="onTimeSelect" format24h now-btn
                      :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                      class="tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-white dark:tw-bg-namaBackground-dim-dark">
                <div class="tw-bg-transparent row items-center justify-end">
                  <q-btn v-close-popup :label="Translator.translateId('close')"
                         class="tw-text-primary-light dark:tw-text-primary-dark tw-bg-transparent" flat/>
                </div>
              </q-time>
            </q-popup-proxy>
          </q-avatar>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>
<style>

</style>
